import React, { useCallback, useState } from "react";

import { Stack } from "@mui/material";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import { useGetTransitionDetailsDto } from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useGetTransitionDetailsDto";
import useUpdateTransition from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useUpdateTransition";

import TransitionWorkflows from "../../TransitionWorkflows";
import {
  EditTransitionFormContent,
  transformFormDataForSave,
} from "../EditTransition/EditTransitionForm";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";

import TransitionsDetails from "./TransitionsDetails";

import styles from "@src/multiCustodian/pages/Advisor/Tasks/CreateTask/CreateTask.module.css";

interface TransitionsDetailsStandaloneProps {
  disableDrawerTransition?: boolean;
}

const TransitionsDetailsStandalone: React.FC<
  TransitionsDetailsStandaloneProps
> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transitionId } = useParams<{ transitionId: string }>();

  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(true);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [formInstance, setFormInstance] = useState<any>(null);

  const statusNotification = useStatusNotification();
  const auth = useAdvisorRequestAuth();
  const updateTransition = useUpdateTransition(auth);
  const {
    data: transitionDetails,
    isLoading,
    hasError,
  } = useGetTransitionDetailsDto(transitionId!, auth);

  const handleNavigateToTransitionsList = (): void => {
    navigate({
      pathname: "/Advisor/Transitions",
      search: location.search,
    });
  };

  const handleCloseDrawer = (): void => {
    setIsDrawerOpen(false);
  };

  const handleEdit = (): void => {
    setIsEditMode(true);
  };

  const handleSave = useCallback(async (): Promise<void> => {
    if (formInstance) {
      const isValid = await formInstance.trigger();
      if (isValid) {
        const formData = formInstance.getValues();

        const transformedData = transformFormDataForSave(formData);

        try {
          await updateTransition({
            transitionId: transitionId,
            request: transformedData,
          });
          statusNotification("Transition edited successfully.", "Success");
        } catch (error) {
          console.error(error);
          statusNotification("Failed to edit transition.", "Error");
        } finally {
          setIsEditMode(false);
        }
      }
    }
  }, [formInstance, statusNotification, transitionId, updateTransition]);

  const handleCancel = (): void => {
    setIsEditMode(false);
  };

  const handleFormReady = useCallback((form: any) => {
    setFormInstance(form);
  }, []);

  const renderFooter = () => {
    if (isLoading) {
      return null;
    }

    if (isEditMode) {
      return (
        <Stack
          sx={{ width: "100%" }}
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={2}
        >
          <div className={styles.actions}>
            <Button
              variant="outlined"
              buttonType="secondary"
              text="Cancel"
              onClick={handleCancel}
            />
            <Button
              variant="contained"
              buttonType="primary"
              text="Save"
              onClick={handleSave}
            />
          </div>
        </Stack>
      );
    }

    return (
      <Stack
        sx={{ width: "100%" }}
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <div className={styles.actions}>
          <Button
            variant="outlined"
            buttonType="primary"
            text="Edit"
            onClick={handleEdit}
          />
        </div>
      </Stack>
    );
  };

  return (
    <TransitionWorkflows>
      <Drawer
        isDrawerOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        SlideProps={{
          onExited: handleNavigateToTransitionsList,
        }}
        footer={renderFooter()}
      >
        {isEditMode ? (
          <EditTransitionFormContent
            transitionId={transitionId!}
            onFormReady={handleFormReady}
          />
        ) : (
          <TransitionsDetails
            transitionDetails={transitionDetails}
            isLoading={isLoading}
            hasError={hasError}
          />
        )}
      </Drawer>
    </TransitionWorkflows>
  );
};

export default TransitionsDetailsStandalone;
